import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FileText, ImagePlus, Upload } from 'lucide-react'
import {
  ETICHETE_FEL_DOCUMENT,
  ETICHETE_FEL_DOCUMENT_SG,
  FELURI_ACT_CLIENT,
  ZILE_ANUNT_EXPIRARE_ACT,
  deCeNuSeIncarca,
  ghicesteFelDocument,
  type FelActClient,
} from '@b4l/shared'
import { api } from '../../lib/api'
import { micsoreazaPoza } from '../../lib/micsoreazaPoza'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { Pastila } from '../../components/ui/Pastila'
import { PanouLateral } from '../../components/ui/PanouLateral'
import { StareLista } from '../../components/ui/StareLista'
import { useConfirmare } from '../../components/ui/Confirmare'
import type { Ton } from '../../components/ui/TonuriB4L'
import { dataScurta, eroare } from './comun'
import { deschideFisier, type Fisa } from './ClientPage'

/*
  Actele clientului (Victor, 19.10.2026: „planograme, contracte, anexe, alte intelegeri"; Adrian:
  „prestari servicii"). Fiecare act are valabilitate; cu 30 de zile inainte de expirare CRM-ul
  anunta agentul si pe cine l-a adaugat. Fisierul se trage in fereastra, felul se ghiceste din
  nume si se poate schimba.
*/

interface Act {
  id: number
  fel: string
  titlu: string
  numeFisier: string
  mimeType: string
  octeti: number
  miniatura: string | null
  valabilDe: string | null
  valabilPana: string | null
  nota: string | null
  punct: { id: number; nume: string } | null
  adaugatDe: string
  adaugatLa: string
  stare: 'activ' | 'expira' | 'expirat' | 'fara_termen'
  sursa?: { id: number; denumire: string } | null
}

const STARE: Record<Act['stare'], { text: string; ton: Ton }> = {
  activ: { text: 'activ', ton: 'verde' },
  expira: { text: 'expiră curând', ton: 'galben' },
  expirat: { text: 'expirat', ton: 'gri' },
  fara_termen: { text: 'fără termen', ton: 'gri' },
}

type Puncte = Fisa['puncte']

function felPentruAct(nume: string, mime: string): FelActClient {
  const f = ghicesteFelDocument(nume, mime)
  return (FELURI_ACT_CLIENT as readonly string[]).includes(f) ? (f as FelActClient) : 'altul'
}

function useActe(id: number) {
  return useQuery<{ acte: Act[]; poze: Act[] }>({
    queryKey: ['clienti', 'acte', id],
    queryFn: async () => (await api.get(`/clienti/${id}/acte`)).data,
  })
}

export function FilaActe({
  id,
  puncte,
  poateScrie,
}: {
  id: number
  puncte: Puncte
  poateScrie: boolean
}) {
  const q = useActe(id)
  const [fel, setFel] = useState<'toate' | FelActClient>('toate')
  const [expirate, setExpirate] = useState(false)
  const [adaug, setAdaug] = useState<File[] | null>(null)
  const [deschis, setDeschis] = useState<number | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const acte = q.data?.acte ?? []
  const vizibile = acte
    .filter((a) => fel === 'toate' || a.fel === fel)
    .filter((a) => expirate || a.stare !== 'expirat')
  const nrExpirate = acte.filter((a) => a.stare === 'expirat').length
  const act = acte.find((a) => a.id === deschis) ?? null

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {(['toate', ...FELURI_ACT_CLIENT] as const).map((k) => {
          const n = k === 'toate' ? acte.length : acte.filter((a) => a.fel === k).length
          if (k !== 'toate' && n === 0) return null
          return (
            <button
              key={k}
              type="button"
              aria-pressed={fel === k}
              onClick={() => setFel(k)}
              className={`rounded-full border px-3 py-1 text-corp ${fel === k ? 'border-b4l-blue bg-b4l-blue-light font-semibold text-b4l-blue-dark' : 'border-b4l-border text-b4l-text-muted hover:text-b4l-text'}`}
            >
              {k === 'toate' ? 'Toate' : ETICHETE_FEL_DOCUMENT[k]} · {n}
            </button>
          )
        })}
        {nrExpirate > 0 && (
          <label className="ml-auto flex items-center gap-2 text-corp text-b4l-text">
            <input
              type="checkbox"
              checked={expirate}
              onChange={(e) => setExpirate(e.target.checked)}
            />
            Arată și cele expirate ({nrExpirate})
          </label>
        )}
        {poateScrie && (
          <Button onClick={() => input.current?.click()} className={nrExpirate ? '' : 'ml-auto'}>
            + Adaugă act
          </Button>
        )}
        <input
          ref={input}
          type="file"
          multiple
          hidden
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
          onChange={(e) => {
            const f = [...(e.target.files ?? [])]
            if (f.length) setAdaug(f)
            e.target.value = ''
          }}
        />
      </div>

      <StareLista
        interogare={q}
        gol={vizibile.length === 0 && !poateScrie}
        mesajGol="Niciun act. Contractele, anexele și planogramele le adaugă agentul sau Operaționalul."
      >
        <div className="grid gap-2">
          {vizibile.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setDeschis(a.id)}
              className={`flex items-center gap-3 rounded-xl border bg-b4l-surface px-3.5 py-3 text-left ${deschis === a.id ? 'border-b4l-blue' : 'border-b4l-border hover:border-b4l-blue'}`}
            >
              <span className="grid h-12 w-10 shrink-0 place-items-center rounded-md border border-b4l-border bg-b4l-bg-soft text-[10px] font-bold uppercase text-b4l-text-muted">
                {a.numeFisier.split('.').pop()?.slice(0, 4) ?? 'doc'}
              </span>
              <span className="grid min-w-0 flex-1 gap-1">
                <span className="flex flex-wrap items-center gap-2">
                  <Pastila ton="gri">
                    {ETICHETE_FEL_DOCUMENT_SG[a.fel as FelActClient] ?? 'Document'}
                  </Pastila>
                  <b className="truncate text-[14px] font-semibold text-b4l-text">{a.titlu}</b>
                </span>
                <span className="text-mic text-b4l-text-muted">
                  {[
                    a.valabilDe || a.valabilPana
                      ? `valabil ${dataScurta(a.valabilDe) === '—' ? '' : `${dataScurta(a.valabilDe)} – `}${dataScurta(a.valabilPana)}`
                      : null,
                    a.punct?.nume,
                    `adăugat de ${a.adaugatDe}, ${dataScurta(a.adaugatLa)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <Pastila ton={STARE[a.stare].ton}>{STARE[a.stare].text}</Pastila>
            </button>
          ))}
          {poateScrie && (
            <ZonaTragere
              text="Trage aici PDF-uri, Word, Excel sau poze ale actelor. Felul actului se ghicește din nume și se poate schimba."
              laFisiere={setAdaug}
            />
          )}
        </div>
      </StareLista>

      {adaug && (
        <ActNou id={id} fisiere={adaug} puncte={puncte} laInchidere={() => setAdaug(null)} />
      )}
      {act && (
        <PanouAct
          id={id}
          act={act}
          puncte={puncte}
          poateScrie={poateScrie}
          laInchidere={() => setDeschis(null)}
        />
      )}
    </div>
  )
}

function ZonaTragere({ text, laFisiere }: { text: string; laFisiere: (f: File[]) => void }) {
  const [peste, setPeste] = useState(false)
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        setPeste(true)
      }}
      onDragLeave={() => setPeste(false)}
      onDrop={(e) => {
        e.preventDefault()
        setPeste(false)
        const f = [...e.dataTransfer.files]
        if (f.length) laFisiere(f)
      }}
      className={`flex items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-5 text-center text-corp ${peste ? 'border-b4l-blue bg-b4l-blue-light text-b4l-blue-dark' : 'border-b4l-border text-b4l-text-muted'}`}
    >
      <Upload size={16} aria-hidden="true" />
      {text}
    </div>
  )
}

/** Fereastra de adaugare: cate un rand pe fisier, cu felul ghicit; valabilitatea e comuna. */
function ActNou({
  id,
  fisiere,
  puncte,
  laInchidere,
}: {
  id: number
  fisiere: File[]
  puncte: Puncte
  laInchidere: () => void
}) {
  const qc = useQueryClient()
  const [randuri, setRanduri] = useState(() =>
    fisiere.map((f) => ({
      f,
      fel: felPentruAct(f.name, f.type),
      titlu: f.name.replace(/\.[a-z0-9]+$/i, ''),
    })),
  )
  const [de, setDe] = useState('')
  const [pana, setPana] = useState('')
  const [punct, setPunct] = useState('')
  const [nota, setNota] = useState('')
  const [urc, setUrc] = useState(false)
  const probleme = randuri.map((r) => deCeNuSeIncarca(r.fel, r.f.type, r.f.size))
  const camp =
    'w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text'

  const urca = async () => {
    if (pana && de && pana < de) {
      toast.error('Sfârșitul valabilității e înaintea începutului.')
      return
    }
    setUrc(true)
    let reusite = 0
    const cazute: string[] = []
    for (const [i, r] of randuri.entries()) {
      if (probleme[i]) continue
      const fd = new FormData()
      fd.append('file', r.f)
      fd.append('fel', r.fel)
      fd.append('titlu', r.titlu)
      if (de) fd.append('valabilDe', de)
      if (pana) fd.append('valabilPana', pana)
      if (punct) fd.append('branchId', punct)
      if (nota.trim()) fd.append('nota', nota.trim())
      try {
        await api.post(`/clienti/${id}/acte`, fd, { timeout: 120_000 })
        reusite++
      } catch (e) {
        cazute.push(`${r.f.name}: ${eroare(e, 'nu s-a încărcat')}`)
      }
    }
    setUrc(false)
    void qc.invalidateQueries({ queryKey: ['clienti', 'acte', id] })
    void qc.invalidateQueries({ queryKey: ['clienti', 'fisa', id] })
    void qc.invalidateQueries({ queryKey: ['clienti', 'activitate', id] })
    if (reusite)
      toast.success(reusite === 1 ? 'Actul a fost adăugat.' : `${reusite} acte adăugate.`)
    if (cazute.length) toast.error(cazute.join(' · '), { duration: 12000 })
    else laInchidere()
  }

  return (
    <Modal
      open
      onClose={laInchidere}
      size="lg"
      title={randuri.length === 1 ? 'Adaugă act' : `Adaugă ${randuri.length} acte`}
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          <Button onClick={() => void urca()} loading={urc} disabled={probleme.every(Boolean)}>
            Salvează
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-corp">
        {randuri.map((r, i) => (
          <div
            key={i}
            className="grid gap-2 rounded-lg border border-b4l-border p-2.5 sm:grid-cols-[minmax(0,1fr)_170px]"
          >
            <label className="grid gap-1">
              <span className="text-mic text-b4l-text-muted">{r.f.name}</span>
              <input
                value={r.titlu}
                maxLength={200}
                onChange={(e) =>
                  setRanduri((x) =>
                    x.map((y, j) => (j === i ? { ...y, titlu: e.target.value } : y)),
                  )
                }
                aria-label={`Titlul pentru ${r.f.name}`}
                className={camp}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-mic text-b4l-text-muted">Fel</span>
              <select
                value={r.fel}
                onChange={(e) =>
                  setRanduri((x) =>
                    x.map((y, j) => (j === i ? { ...y, fel: e.target.value as FelActClient } : y)),
                  )
                }
                className={camp}
              >
                {FELURI_ACT_CLIENT.map((k) => (
                  <option key={k} value={k}>
                    {ETICHETE_FEL_DOCUMENT_SG[k]}
                  </option>
                ))}
              </select>
            </label>
            {probleme[i] && (
              <p className="text-mic text-b4l-red-dark sm:col-span-2">
                Nu se poate încărca: {probleme[i]}
              </p>
            )}
          </div>
        ))}
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="grid gap-1">
            <span className="text-mic font-semibold text-b4l-text-muted">Valabil de la</span>
            <input
              type="date"
              value={de}
              onChange={(e) => setDe(e.target.value)}
              className={camp}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-mic font-semibold text-b4l-text-muted">Valabil până la</span>
            <input
              type="date"
              value={pana}
              onChange={(e) => setPana(e.target.value)}
              className={camp}
            />
          </label>
        </div>
        <p className="text-mic text-b4l-text-dim">
          Fără „până la” = fără termen. Cu termen, CRM-ul anunță agentul cu{' '}
          {ZILE_ANUNT_EXPIRARE_ACT} de zile înainte.
        </p>
        {puncte.length > 0 && (
          <label className="grid gap-1">
            <span className="text-mic font-semibold text-b4l-text-muted">Punct de lucru</span>
            <select value={punct} onChange={(e) => setPunct(e.target.value)} className={camp}>
              <option value="">Toate punctele</option>
              {puncte.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.branchName}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="grid gap-1">
          <span className="text-mic font-semibold text-b4l-text-muted">
            Ce s-a agreat (opțional)
          </span>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="ex. taxa de listare 1.500 lei/lună"
            className={camp}
          />
        </label>
      </div>
    </Modal>
  )
}

function PanouAct({
  id,
  act,
  puncte,
  poateScrie,
  laInchidere,
}: {
  id: number
  act: Act
  puncte: Puncte
  poateScrie: boolean
  laInchidere: () => void
}) {
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const [editez, setEditez] = useState(false)
  const [v, setV] = useState({
    titlu: act.titlu,
    fel: act.fel as FelActClient,
    de: act.valabilDe ?? '',
    pana: act.valabilPana ?? '',
    punct: act.punct ? String(act.punct.id) : '',
    nota: act.nota ?? '',
  })
  const reimprospateaza = () => {
    void qc.invalidateQueries({ queryKey: ['clienti', 'acte', id] })
    void qc.invalidateQueries({ queryKey: ['clienti', 'fisa', id] })
  }
  const salveaza = useMutation({
    mutationFn: async () =>
      api.patch(`/clienti/${id}/acte/${act.id}`, {
        titlu: v.titlu,
        fel: v.fel,
        valabilDe: v.de || null,
        valabilPana: v.pana || null,
        branchId: v.punct ? Number(v.punct) : null,
        nota: v.nota || null,
      }),
    onSuccess: () => {
      toast.success('Salvat.')
      setEditez(false)
      reimprospateaza()
    },
    onError: (e) => toast.error(eroare(e)),
  })
  const sterge = useMutation({
    mutationFn: async () => api.delete(`/clienti/${id}/acte/${act.id}`),
    onSuccess: () => {
      toast.success('Actul a fost șters.')
      reimprospateaza()
      laInchidere()
    },
    onError: (e) => toast.error(eroare(e)),
  })
  const camp =
    'w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text'
  return (
    <PanouLateral
      deschis
      onInchide={laInchidere}
      titlu={act.titlu}
      subtitlu={<Pastila ton={STARE[act.stare].ton}>{STARE[act.stare].text}</Pastila>}
    >
      <div className="grid gap-4 text-corp">
        <button
          type="button"
          onClick={() => void deschideFisier(id, act.id)}
          className="flex items-center gap-2 rounded-caseta border border-b4l-border bg-b4l-bg-soft px-3 py-3 text-left text-b4l-text hover:border-b4l-blue"
        >
          <FileText size={18} className="text-b4l-text-muted" aria-hidden="true" />
          <span>
            Deschide documentul
            <span className="block text-mic text-b4l-text-dim">
              {act.numeFisier} · {Math.max(1, Math.round(act.octeti / 1024))} KB
            </span>
          </span>
        </button>
        {!editez ? (
          <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2">
            <dt className="text-b4l-text-muted">Fel</dt>
            <dd>{ETICHETE_FEL_DOCUMENT_SG[act.fel as FelActClient] ?? 'Document'}</dd>
            <dt className="text-b4l-text-muted">Valabil</dt>
            <dd>
              {act.valabilDe || act.valabilPana
                ? `${dataScurta(act.valabilDe)} – ${dataScurta(act.valabilPana)}`
                : 'fără termen'}
            </dd>
            <dt className="text-b4l-text-muted">Punct de lucru</dt>
            <dd>{act.punct?.nume ?? 'toate punctele'}</dd>
            <dt className="text-b4l-text-muted">Ce s-a agreat</dt>
            <dd className="whitespace-pre-line">{act.nota ?? '—'}</dd>
            <dt className="text-b4l-text-muted">Adăugat</dt>
            <dd>
              {act.adaugatDe}, {dataScurta(act.adaugatLa)}
            </dd>
          </dl>
        ) : (
          <div className="grid gap-2.5">
            <label className="grid gap-1">
              <span className="text-mic font-semibold text-b4l-text-muted">Titlu</span>
              <input
                value={v.titlu}
                maxLength={200}
                onChange={(e) => setV({ ...v, titlu: e.target.value })}
                className={camp}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-mic font-semibold text-b4l-text-muted">Fel</span>
              <select
                value={v.fel}
                onChange={(e) => setV({ ...v, fel: e.target.value as FelActClient })}
                className={camp}
              >
                {FELURI_ACT_CLIENT.map((k) => (
                  <option key={k} value={k}>
                    {ETICHETE_FEL_DOCUMENT_SG[k]}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="grid gap-1">
                <span className="text-mic font-semibold text-b4l-text-muted">De la</span>
                <input
                  type="date"
                  value={v.de}
                  onChange={(e) => setV({ ...v, de: e.target.value })}
                  className={camp}
                />
              </label>
              <label className="grid gap-1">
                <span className="text-mic font-semibold text-b4l-text-muted">Până la</span>
                <input
                  type="date"
                  value={v.pana}
                  onChange={(e) => setV({ ...v, pana: e.target.value })}
                  className={camp}
                />
              </label>
            </div>
            {puncte.length > 0 && (
              <label className="grid gap-1">
                <span className="text-mic font-semibold text-b4l-text-muted">Punct de lucru</span>
                <select
                  value={v.punct}
                  onChange={(e) => setV({ ...v, punct: e.target.value })}
                  className={camp}
                >
                  <option value="">Toate punctele</option>
                  {puncte.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.branchName}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="grid gap-1">
              <span className="text-mic font-semibold text-b4l-text-muted">Ce s-a agreat</span>
              <textarea
                rows={3}
                value={v.nota}
                maxLength={1000}
                onChange={(e) => setV({ ...v, nota: e.target.value })}
                className={camp}
              />
            </label>
          </div>
        )}
        {poateScrie && (
          <div className="flex flex-wrap gap-2">
            {editez ? (
              <>
                <Button
                  onClick={() => salveaza.mutate()}
                  loading={salveaza.isPending}
                  disabled={!!v.de && !!v.pana && v.pana < v.de}
                >
                  Salvează
                </Button>
                <Button variant="secondary" onClick={() => setEditez(false)}>
                  Renunță
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setEditez(true)}>
                  Editează
                </Button>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    if (
                      await confirma({
                        titlu: 'Ștergi actul?',
                        mesaj: 'Se șterge și fișierul. Nu se poate desface.',
                        buton: 'Șterge',
                        pericol: true,
                      })
                    )
                      sterge.mutate()
                  }}
                >
                  Șterge
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </PanouLateral>
  )
}

// ─── Poze și expuneri ──────────────────────────────────────────────────────────

export function FilaPoze({ id, poateScrie }: { id: number; puncte: Puncte; poateScrie: boolean }) {
  const q = useActe(id)
  const qc = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [urc, setUrc] = useState(false)
  const poze = useMemo(() => q.data?.poze ?? [], [q.data])

  const urca = async (fisiere: File[]) => {
    setUrc(true)
    let reusite = 0
    for (const f of fisiere) {
      try {
        const m = await micsoreazaPoza(f)
        const fd = new FormData()
        fd.append('file', m.fisier)
        fd.append('miniatura', m.miniatura)
        fd.append('fel', 'poza')
        await api.post(`/clienti/${id}/acte`, fd, { timeout: 120_000 })
        reusite++
      } catch (e) {
        toast.error(`${f.name}: ${eroare(e, 'nu s-a încărcat (merg JPG, PNG, WEBP)')}`)
      }
    }
    setUrc(false)
    if (reusite)
      toast.success(reusite === 1 ? 'Poza a fost adăugată.' : `${reusite} poze adăugate.`)
    void qc.invalidateQueries({ queryKey: ['clienti', 'acte', id] })
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-corp text-b4l-text-muted">
          Pozele clientului, plus cele din expunerile și acțiunile din farmacia lui.
        </p>
        {poateScrie && (
          <Button onClick={() => input.current?.click()} loading={urc}>
            <ImagePlus size={15} aria-hidden="true" /> Adaugă poze
          </Button>
        )}
        <input
          ref={input}
          type="file"
          multiple
          hidden
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => {
            const f = [...(e.target.files ?? [])]
            if (f.length) void urca(f)
            e.target.value = ''
          }}
        />
      </div>
      <StareLista
        interogare={q}
        gol={poze.length === 0}
        mesajGol="Nicio poză încă. Pozele din expuneri apar aici singure."
      >
        <ul className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(160px,1fr))]">
          {poze.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => void deschideFisier(id, p.id)}
                className="grid w-full gap-1 rounded-lg border border-b4l-border bg-b4l-surface p-1.5 text-left hover:border-b4l-blue"
              >
                {p.miniatura ? (
                  <img
                    src={p.miniatura}
                    alt={p.titlu}
                    className="aspect-square w-full rounded object-cover"
                  />
                ) : (
                  <span className="grid aspect-square w-full place-items-center rounded bg-b4l-bg-soft text-mic text-b4l-text-dim">
                    fără miniatură
                  </span>
                )}
                <span className="truncate px-0.5 text-mic text-b4l-text">
                  {p.sursa ? p.sursa.denumire : p.titlu}
                </span>
                <span className="px-0.5 text-[11px] text-b4l-text-dim">
                  {dataScurta(p.adaugatLa)} · {p.adaugatDe}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </StareLista>
    </div>
  )
}
